import React, { Fragment } from 'react'
import { Formik, Field } from 'formik'

// utils
import checkRoles from 'utils/sef/checkRoles'
import logos from 'utils/logos'
import checkPermissions from 'utils/sef/checkRoutingPermissions'

// styles
import {
  BrowniePointsLogo,
  DashboardMobileHeading,
  DashboardHeading,
  DashboardHomePageMobileContainer,
  DashboardSideNavOuter,
  DashCollapseIcon,
  DashboardSettingsWrapper,
  DashSidebarConfigOption,
  DashSettingsIcon,
  FooterLink,
  NavItems,
  SidebarFooterLogoWrapper,
  SidebarFooter
} from './styles'

// assets
import { ReactComponent as HasLocationIcon } from 'assets/images/icons/opp-location.svg'
import { SolidHeart } from 'components/Icons/Icons'
import { ReactComponent as OppIcon } from 'assets/images/svgs/icon-org-page-opportunity.svg'
import { ReactComponent as DonationIcon } from 'assets/images/cause-type-icons/money_icon_dark.svg'
import { ReactComponent as OrgIcon } from 'assets/images/svgs/icon-org-page-organisation.svg'
import { ReactComponent as BpEventsIcon } from 'assets/images/svgs/icon-bp-events.svg'
import { ReactComponent as EmailActivityIcon } from 'assets/images/svgs/icon-email-activity.svg'

// config
import constants from 'config/constants'
import l, { c } from 'config/localization'

// icons
import { AppFolder } from '@styled-icons/fluentui-system-filled/AppFolder'
import { BuildingHouse } from '@styled-icons/boxicons-regular/BuildingHouse'
import { BusinessCenter } from '@styled-icons/material-rounded/BusinessCenter'
import { Dashboard } from '@styled-icons/boxicons-solid'
import { DashboardSideNav, DashboardNavItem } from '../Dashboard'
import { DataArea } from '@styled-icons/fluentui-system-regular/DataArea'
import { Handshake } from '@styled-icons/fa-solid/Handshake'
import { Boxes, PeopleFill as People, Receipt } from '@styled-icons/bootstrap'
import { Results } from '@styled-icons/foundation/Results'
import { ArchiveDrawer, Team } from '@styled-icons/remix-fill'
import { Tags } from '@styled-icons/fa-solid/Tags'
import { AdminPanelSettings } from '@styled-icons/material/AdminPanelSettings'
import { Wrench } from 'components/Icons/Icons'
import { Workspaces } from '@styled-icons/material/Workspaces'
import { Rule } from '@styled-icons/material-rounded/Rule'
import { DocumentPdf } from '@styled-icons/fluentui-system-regular/DocumentPdf'
import { FileInvoiceDollar } from '@styled-icons/fa-solid/FileInvoiceDollar'
import { JournalText } from '@styled-icons/bootstrap/JournalText'
import { GiftFill } from '@styled-icons/bootstrap/GiftFill'
import { Calendar2Range } from '@styled-icons/bootstrap/Calendar2Range'

// Load components synchronously
import CheckboxInput from 'components/CheckboxInput'
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import SmallButtonPrimary from 'components/SmallButtonPrimary/SmallButtonPrimary'

const { dashSidebarLogo } = logos

const SefSidebar = ({
  dashboardSettingsVisible,
  hasFetchedRoles,
  locationInfo,
  roles = [],
  sidebarIsCollapsed,
  toggleSidebarCollapse,
  toggleDashSidebarSettings,
  workspaceDetails = [],
  updateUser,
  user
}) => {
  const sefRoles = checkRoles({ roleData: roles })

  const canViewAgreements = checkPermissions({
    featureName: 'agreements',
    sefRoles,
    workspaceDetails
  })
  const canViewAttendance = checkPermissions({
    featureName: 'attendances',
    roles,
    sefRoles,
    workspaceDetails
  })
  const canViewBudgetCategories = checkPermissions({
    featureName: 'budgets',
    sefRoles,
    workspaceDetails
  })
  const canViewPurchases = checkPermissions({
    featureName: 'purchases',
    sefRoles,
    workspaceDetails
  })
  const canViewSuppliers = checkPermissions({
    featureName: 'suppliers',
    sefRoles,
    workspaceDetails
  })
  const canViewDistricts = checkPermissions({
    featureName: 'districts',
    sefRoles,
    workspaceDetails
  })
  const canViewOutputAreas = checkPermissions({
    featureName: 'outputs',
    sefRoles,
    workspaceDetails
  })
  const canViewIndicators = checkPermissions({
    featureName: 'indicators',
    sefRoles,
    workspaceDetails
  })
  const canViewOutputs = checkPermissions({
    featureName: 'outputs',
    sefRoles,
    workspaceDetails
  })
  const canViewWorksites = checkPermissions({
    featureName: 'worksites',
    sefRoles,
    workspaceDetails
  })
  const canViewParticipants = checkPermissions({
    featureName: 'participants',
    sefRoles,
    workspaceDetails
  })
  const canViewPayroll = checkPermissions({
    featureName: 'payroll',
    sefRoles,
    workspaceDetails
  })

  const canViewPartners =
    sefRoles.isGodAdmin || sefRoles.isWorkspaceAdmin || sefRoles.isWorkspaceProjectAdmin

  const canViewAdmins =
    sefRoles.isGodAdmin ||
    sefRoles.isSuperAdmin ||
    sefRoles.isPartnerAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceProjectAdmin ||
    sefRoles.isWorkspaceSigningAdmin

  const canViewPrograms = sefRoles.isGodAdmin || sefRoles.isPlatformAdmin
  const canViewWorkspaces = sefRoles.isGodAdmin || sefRoles.isPlatformAdmin
  const canViewProjects =
    sefRoles.isGodAdmin ||
    sefRoles.isPlatformAdmin ||
    sefRoles.isWorkspaceAdmin ||
    sefRoles.isWorkspaceProjectAdmin

  const hasPrivilegedRole = sefRoles.isPlatformAdmin || sefRoles.hasGodRole
  const hasPrivilegedOrSuperAdminRole = hasPrivilegedRole || sefRoles.isSuperAdmin
  const dashboardConfig = user.dashboardConfig || {}

  if (!hasFetchedRoles) {
    return (
      <Fragment>
        <DashboardHomePageMobileContainer displayMobile={locationInfo.homeActive}>
          <DashboardMobileHeading>Dashboard Menu</DashboardMobileHeading>
        </DashboardHomePageMobileContainer>

        <DashboardSideNavOuter displayMobile={locationInfo.homeActive}>
          <DashboardSideNav displayMobile={locationInfo.homeActive}>
            <NavItems id="dashboard-menu-items-wrapper">
              <DashboardNavItem
                hiddenOnMobile={locationInfo.homeActive}
                Icon={Dashboard}
                hover="exception"
                id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
                isActive={locationInfo.homeActive}
                label="Dashboard"
                to="/dashboard"
              />
            </NavItems>
          </DashboardSideNav>

          <SidebarFooterLogoWrapper>
            <FooterLink to="/">
              <BrowniePointsLogo src={dashSidebarLogo} />
            </FooterLink>
          </SidebarFooterLogoWrapper>
        </DashboardSideNavOuter>
      </Fragment>
    )
  }

  if (dashboardSettingsVisible) {
    return (
      <Fragment>
        <DashboardHeading>Select the dashboard navigation items you want to see</DashboardHeading>

        <DashboardSideNavOuter dashboardSettingsVisible displayMobile={locationInfo.homeActive}>
          <DashboardSettingsWrapper>
            <Formik
              initialValues={{
                user_sidebar_config_update: true,
                user_id: user.id,
                ...dashboardConfig
              }}
              enableReinitialize
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={values => {
                updateUser(values)
                toggleDashSidebarSettings()
              }}
              render={({ handleSubmit }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      <DashSidebarConfigOption>
                        <Field disabled name="dash_sidenav_home" component={CheckboxInput} />
                        <Dashboard />
                        <span>Dashboard Home</span>
                      </DashSidebarConfigOption>

                      <ConditionalDisplay displayWhen={[hasPrivilegedRole]}>
                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_email_activity" component={CheckboxInput} />
                          <EmailActivityIcon />
                          <span>Email activity</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <ConditionalDisplay displayWhen={[false, hasPrivilegedRole]}>
                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_playbooks" component={CheckboxInput} />
                          <EmailActivityIcon />
                          <span>Playbooks</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <ConditionalDisplay displayWhen={[hasPrivilegedRole]}>
                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_bp_events" component={CheckboxInput} />
                          <BpEventsIcon />
                          <span>BP events</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_stories" component={CheckboxInput} />
                        <JournalText />
                        <span>Stories</span>
                      </DashSidebarConfigOption>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_contributions" component={CheckboxInput} />
                        <GiftFill />
                        <span>Contributions</span>
                      </DashSidebarConfigOption>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_likes" component={CheckboxInput} />
                        <SolidHeart />
                        <span>Likes</span>
                      </DashSidebarConfigOption>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_memberships" component={CheckboxInput} />
                        <DonationIcon />
                        <span>Memberships</span>
                      </DashSidebarConfigOption>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_opps" component={CheckboxInput} />
                        <OppIcon />
                        <span>{c(l('OPPS'))}</span>
                      </DashSidebarConfigOption>

                      <ConditionalDisplay displayWhen={[hasPrivilegedOrSuperAdminRole]}>
                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_camps" component={CheckboxInput} />
                          <Calendar2Range />
                          <span>{c(l('CAMPS'))}</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <DashSidebarConfigOption>
                        <Field name="dash_sidenav_orgs" component={CheckboxInput} />
                        <OrgIcon />
                        <span>{c(l('ORGS'))}</span>
                      </DashSidebarConfigOption>

                      <ConditionalDisplay displayWhen={[hasPrivilegedOrSuperAdminRole]}>
                        <DashSidebarConfigOption>
                          <Field name="dash_sidenav_people" component={CheckboxInput} />
                          <People />
                          <span>People</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <ConditionalDisplay displayWhen={[sefRoles.isSuperAdmin]}>
                        <DashSidebarConfigOption>
                          <Field disabled name="dash_sidenav_billing" component={CheckboxInput} />
                          <Receipt />
                          <span>Billing</span>
                        </DashSidebarConfigOption>
                      </ConditionalDisplay>

                      <DashSidebarConfigOption>
                        <Field disabled name="dash_sidenav_account" component={CheckboxInput} />
                        <Wrench />
                        <span>Account</span>
                      </DashSidebarConfigOption>
                    </div>

                    <div style={{ marginBottom: '10px', marginTop: '20px' }}>
                      <DashSidebarConfigOption>
                        <Field
                          name="dash_show_completed_and_dismissed_onboarding_tasks"
                          component={CheckboxInput}
                        />

                        <span>Show completed and dismissed onboarding challenges</span>
                      </DashSidebarConfigOption>
                    </div>

                    <div style={{ display: 'flex', marginTop: '10px' }}>
                      <div style={{ marginRight: '5px' }}>
                        <SmallButtonPrimary type="submit">Save</SmallButtonPrimary>
                      </div>

                      <LinkButton onClick={toggleDashSidebarSettings} type="button">
                        Cancel
                      </LinkButton>
                    </div>
                  </form>
                )
              }}
            />
          </DashboardSettingsWrapper>
        </DashboardSideNavOuter>
      </Fragment>
    )
  }

  return (
    <Fragment>
      <DashboardHomePageMobileContainer displayMobile={locationInfo.homeActive}>
        <DashboardMobileHeading>Dashboard Menu</DashboardMobileHeading>
      </DashboardHomePageMobileContainer>

      <DashboardSideNavOuter
        displayMobile={locationInfo.homeActive}
        sidebarIsCollapsed={sidebarIsCollapsed}
      >
        <DashboardSideNav
          displayMobile={locationInfo.homeActive}
          sidebarIsCollapsed={sidebarIsCollapsed}
        >
          <NavItems id="dashboard-menu-items-wrapper">
            <DashboardNavItem
              hiddenOnMobile={locationInfo.homeActive}
              Icon={Dashboard}
              hover="exception"
              id={locationInfo.homeActive ? 'active-dash-nav-item' : undefined}
              isActive={locationInfo.homeActive}
              label="Dashboard"
              sidebarIsCollapsed={sidebarIsCollapsed}
              to="/dashboard"
            />

            <ConditionalDisplay displayWhen={[canViewPrograms]}>
              <DashboardNavItem
                hover="exception"
                Icon={Boxes}
                isActive={locationInfo.programsActive}
                id={locationInfo.programsActive ? 'active-dash-nav-item' : undefined}
                label="Programs"
                sidebarIsCollapsed={sidebarIsCollapsed}
                to={'/dashboard/programs'}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewWorkspaces]}>
              <DashboardNavItem
                hover="exception"
                Icon={Workspaces}
                isActive={locationInfo.workspacesActive}
                id={locationInfo.workspacesActive ? 'active-dash-nav-item' : undefined}
                label="Workspaces"
                sidebarIsCollapsed={sidebarIsCollapsed}
                to={'/dashboard/workspaces'}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewProjects]}>
              <DashboardNavItem
                hover="exception"
                Icon={ArchiveDrawer}
                isActive={locationInfo.projectsActive}
                id={locationInfo.projectsActive ? 'active-dash-nav-item' : undefined}
                label="Projects"
                sidebarIsCollapsed={sidebarIsCollapsed}
                to={'/dashboard/workspace-projects'}
              />
            </ConditionalDisplay>

            {false && (
              <DashboardNavItem
                label="Rules"
                Icon={Rule}
                to={'/dashboard/rules'}
                isActive={locationInfo.rulesActive}
                hover="exception"
                id={locationInfo.rulesActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            )}

            <ConditionalDisplay displayWhen={[canViewAgreements]}>
              <DashboardNavItem
                label="Agreements"
                Icon={DocumentPdf}
                to={'/dashboard/agreements?status=active'}
                isActive={locationInfo.agreementsActive}
                hover="exception"
                id={locationInfo.agreementsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewPartners]}>
              <DashboardNavItem
                label="Partners"
                Icon={Handshake}
                to={'/dashboard/partners'}
                isActive={locationInfo.partnersActive}
                hover="exception"
                id={locationInfo.partnersActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewDistricts]}>
              <DashboardNavItem
                label="Districts"
                Icon={HasLocationIcon}
                to={'/dashboard/districts'}
                isActive={locationInfo.districtsActive}
                id={locationInfo.districtsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewWorksites]}>
              <DashboardNavItem
                label="Worksites"
                Icon={BuildingHouse}
                to={'/dashboard/worksites'}
                isActive={locationInfo.worksitesActive}
                id={locationInfo.worksitesActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewAdmins]}>
              <DashboardNavItem
                label="Admins"
                Icon={AdminPanelSettings}
                hover="exception"
                to={'/dashboard/admins'}
                isActive={locationInfo.adminsActive}
                id={locationInfo.adminsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewParticipants]}>
              <DashboardNavItem
                label="Participants"
                Icon={People}
                hover="exception"
                to={'/dashboard/participants'}
                isActive={locationInfo.participantsActive}
                id={locationInfo.participantsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewAttendance]}>
              <DashboardNavItem
                label="Attendance"
                Icon={Team}
                to={`/dashboard/attendance?payrollPeriod=${constants.CURRENT_DEFAULT_PAYROLL_PERIOD}`}
                isActive={locationInfo.attendanceActive}
                hover="exception"
                id={locationInfo.attendanceActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewOutputs]}>
              <DashboardNavItem
                label="Outputs"
                Icon={Results}
                to={'/dashboard/outputs'}
                isActive={locationInfo.outputsActive}
                hover="exception"
                id={locationInfo.outputsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewOutputAreas]}>
              <DashboardNavItem
                label="Areas"
                hover="exception"
                Icon={AppFolder}
                to={'/dashboard/output-areas'}
                isActive={locationInfo.outputAreasActive}
                id={locationInfo.outputAreasActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewIndicators]}>
              <DashboardNavItem
                label="Indicators"
                hover="exception"
                Icon={DataArea}
                to={'/dashboard/output-indicators'}
                isActive={locationInfo.indicatorsActive}
                id={locationInfo.indicatorsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewSuppliers]}>
              <DashboardNavItem
                label="Suppliers"
                Icon={BusinessCenter}
                to={'/dashboard/suppliers'}
                isActive={locationInfo.suppliersActive}
                hover="exception"
                id={locationInfo.suppliersActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewBudgetCategories]}>
              <DashboardNavItem
                label="Budgets"
                hover="exception"
                Icon={Tags}
                to={'/dashboard/budget-categories'}
                isActive={locationInfo.budgetCategoriesActive}
                id={locationInfo.budgetCategoriesActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewPayroll]}>
              <DashboardNavItem
                label="Payroll"
                Icon={FileInvoiceDollar}
                to={`/dashboard/payroll?payrollPeriod=${constants.CURRENT_DEFAULT_PAYROLL_PERIOD}`}
                isActive={locationInfo.payrollActive}
                hover="exception"
                id={locationInfo.payrollActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <ConditionalDisplay displayWhen={[canViewPurchases]}>
              <DashboardNavItem
                label="Purchases"
                Icon={Receipt}
                to={'/dashboard/purchase-requests'}
                isActive={locationInfo.purchaseRequestsActive}
                hover="exception"
                id={locationInfo.purchaseRequestsActive ? 'active-dash-nav-item' : undefined}
                sidebarIsCollapsed={sidebarIsCollapsed}
              />
            </ConditionalDisplay>

            <DashboardNavItem
              label="Account"
              hover="exception"
              Icon={Wrench}
              to={'/dashboard/account'}
              isActive={locationInfo.accountActive}
              id={locationInfo.accountActive ? 'active-dash-nav-item' : undefined}
              sidebarIsCollapsed={sidebarIsCollapsed}
            />
          </NavItems>
        </DashboardSideNav>

        <SidebarFooter>
          <DashCollapseIcon
            sidebarIsCollapsed={sidebarIsCollapsed}
            toggleSidebarCollapse={toggleSidebarCollapse}
          />

          {false && (
            <DashSettingsIcon
              sidebarIsCollapsed={sidebarIsCollapsed}
              toggleDashSidebarSettings={toggleDashSidebarSettings}
            />
          )}
        </SidebarFooter>
      </DashboardSideNavOuter>
    </Fragment>
  )
}

export default SefSidebar
